import React from 'react';

import { STREAM_DATA_TYPE_OPTIONS } from '@vlabs/api-bindings/src/constants';
import { InfoCircleFilledIcon, PlusIcon } from '@vlabs/icons';
import { ListItem } from '@vlabs/shared/components/list-item/ListItem';
import validate from '@vlabs/shared/validators';
import { Divider, Control } from '@vlabs/uikit';
import i18n from 'i18next';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnalyticsDropdown } from '@vlabs/pages/video-analytics/streams/components/AnalyticsDropdown';
import { AnalyticsArrayProvider, useAnalyticsContext } from '@vlabs/pages/video-analytics/streams/components/forms/useAnalyticsContext';

import st from './SelectVideoAnalyticsModal.module.sass';

function ModalFormProvider({ onSubmit, children }) {
  const form = useForm();

  const handleFormSubmit = (formData) => {
    const { data, ...rest } = formData;

    const payload = {
      ...rest,
      splittable: 1,
      data: {
        ...data,
        downloadable: true,
      },
    };

    onSubmit(payload);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)}>
        {children}
      </form>
    </FormProvider>
  );
}

const analyticLabels = {
  get people_count() {
    return i18n.t('videoAnalytics:event_type.crowd.label');
  },
  get fights_analytics() {
    return i18n.t('videoAnalytics:event_type.fight.label');
  },
  get weapon_analytics() {
    return i18n.t('videoAnalytics:event_type.weapon.label');
  },
  get fire_analytics() {
    return i18n.t('videoAnalytics:event_type.fire.label');
  },
  get facecover_analytics() {
    return i18n.t('videoAnalytics:event_type.facecover.label');
  },
  get bags_analytics() {
    return i18n.t('videoAnalytics:event_type.bag.label');
  },
  get lying_analytics() {
    return i18n.t('videoAnalytics:event_type.lying.label');
  },
  get handsup_analytics() {
    return i18n.t('videoAnalytics:event_type.handsup.label');
  },
  get fall_analytics() {
    return i18n.t('videoAnalytics:event_type.fall.label');
  },
};

function Modal({ closeModal }) {
  const { t } = useTranslation();
  const form = useFormContext({ defaultValues: { analytics: [] } });
  const {
    addAnalytic,
    analyticFields,
    fieldArray,
  } = useAnalyticsContext();

  return (
    <React.Fragment>
      <div className={st.Content}>
        <h5>{t('videoAnalytics:table_header.create_videoanalytics_stream')}</h5>
        <div className={st.Body}>
          <Control.Input
            errors={form.formState.errors}
            isClearable
            label={t('videoAnalytics:table_header.name')}
            {...form?.register('name', {
              maxLength: validate.maxLength(128),
              validate: validate.regex(/^[\p{L}0-9_-]+$/u),
            })}
          />
          <Divider small />

          <Control.Select
            control={form.control}
            errors={form.formState.errors}
            id="data.type"
            label={t('videoAnalytics:editAndCreateStreamsPageTitles.type')}
            name="data.type"
            options={STREAM_DATA_TYPE_OPTIONS.raw}
            rules={{ required: validate.required() }}
          />
          <Divider small />

          <Control.Input
            label={t('videoAnalytics:editAndCreateStreamsPageTitles.full_path_to_stream')}
            {...form.register('data.reference', {
              required: validate.required(),
              maxLength: validate.maxLength(2048),
            })}
            errors={form.formState.errors}
            isClearable
          />

          <Divider />

          <span className="Subtitle-1">{t('video_analytics_title')}</span>
          <div className={st.Divider} />
          {analyticFields.map((analytic, i) => (
            <ListItem
              className={st.ListItem}
              icon={analytic?.icon}
              key={analytic.key}
              label={analytic.labelWithOrdinal}
              onClick={() => fieldArray.remove(i)}
            />
          ))}
          {!analyticFields.length && (
            <div className={st.Hint}>
              <InfoCircleFilledIcon />
              <div>{t('videoAnalytics:form.modal.videoAnalyticsRequiredHint')}</div>
            </div>
          )}

          <AnalyticsDropdown
            onAnalyticClick={(type) => addAnalytic(type)}
            placement="top-left"
            renderTrigger={(props) => (
              <Control.Button
                icon={<PlusIcon />}
                type="button"
                variant="flat"
                {...props}
              >
                {t('btn.add')}
              </Control.Button>
            )}
          />

          <Divider />
        </div>
      </div>
      <div className={st.Footer}>
        <div className={st.Buttons}>
          <Control.Button
            data-testid="saveSelectedAnalyticsButton"
            disabled={analyticFields.length === 0}
            fullWidth
            kind="primary"
            type="submit"
          >
            {t('shared:form.submit_create')}
          </Control.Button>
        </div>
        <div className={st.Buttons}>
          <Control.Button
            data-testid="cancelSelectedAnalyticsButton"
            fullWidth
            kind="secondary"
            onClick={() => closeModal()}
            variant="outlined"
          >
            {t('shared:btn.cancel')}
          </Control.Button>
        </div>
      </div>
    </React.Fragment>
  );
}

export function SelectVideoAnalyticsModal({ closeModal, onSubmit }) {
  return (
    <ModalFormProvider onSubmit={onSubmit}>
      <AnalyticsArrayProvider analyticLabels={analyticLabels}>
        <Modal
          closeModal={closeModal}
          onSubmit={onSubmit}
        />
      </AnalyticsArrayProvider>
    </ModalFormProvider>
  );
}
